import { useQuery } from "@tanstack/react-query";
import { createPublicClient, http, erc20Abi, type Address } from "viem";
import { esIgual, type RedKeel } from "../config/networks";

export type Token = { symbol: string; address: Address; decimals: number };
export type Pool = {
  pool: Address;
  fee: number;
  token0: Token;
  token1: Token;
  pair: string;
  tvlUsd: number;
  aprPct: number;
  // Volumen ANUAL en USD que estima el indexer (fees/feeTier extrapolado). La ficha lo divide entre
  // 365 para enseñar el volumen y las comisiones de 24h. Opcional: el fallback on-chain no lo trae.
  volumeYearUsd?: number;
};

// RPC público por red para el fallback on-chain (cuando el indexer no sirve pools*.json).
// Proxy same-origin del vhost beta (evita el CORS de los RPC públicos en el navegador).
const RPC: Record<RedKeel["clave"], string> = {
  arbitrum: "/rpc/arbitrum/",
  robinhood: "/rpc/robinhood/",
  hyperevm: "/rpc/hyperevm/",
  bnb: "/rpc/bsc/",
  base: "/rpc/base/",
  ethereum: "/rpc/ethereum/",
};

const POOL_ABI = [
  { name: "token0", type: "function", stateMutability: "view", inputs: [], outputs: [{ type: "address" }] },
  { name: "token1", type: "function", stateMutability: "view", inputs: [], outputs: [{ type: "address" }] },
  { name: "fee", type: "function", stateMutability: "view", inputs: [], outputs: [{ type: "uint24" }] },
  { name: "tickSpacing", type: "function", stateMutability: "view", inputs: [], outputs: [{ type: "int24" }] },
] as const;

/**
 * FALLBACK SIN INDEXER (beta): lee los metadatos de las pools curadas directamente de la cadena.
 * No trae TVL ni APR (los calcula el indexer), pero deja crear piscinas igual. Uniswap V3 expone
 * `fee()`; Aerodrome Slipstream expone `tickSpacing()` — se prueban los dos.
 */
// Multicall3: misma dirección canónica en todas las cadenas EVM mayores. Una sola eth_call agrupa
// todas las lecturas — así no hay 48 peticiones sueltas que el rate-limit del RPC público tumbaría.
const MULTICALL3 = "0xcA11bde05977b3631167028862bE2a173976CA11" as Address;

async function poolsOnChain(red: RedKeel): Promise<Pool[]> {
  const pools = red.poolsPermitidas;
  if (!pools.length) return [];
  const client = createPublicClient({ transport: http(RPC[red.clave]) });

  // 1) token0/token1/fee/tickSpacing de todas las pools (fee=UniV3, tickSpacing=Slipstream).
  const r1 = await client.multicall({
    multicallAddress: MULTICALL3,
    allowFailure: true,
    contracts: pools.flatMap((p) => [
      { address: p, abi: POOL_ABI, functionName: "token0" },
      { address: p, abi: POOL_ABI, functionName: "token1" },
      { address: p, abi: POOL_ABI, functionName: "fee" },
      { address: p, abi: POOL_ABI, functionName: "tickSpacing" },
    ]),
  });

  const tokens = new Set<Address>();
  const base = pools.map((p, i) => {
    const t0 = r1[i * 4]?.result as Address | undefined;
    const t1 = r1[i * 4 + 1]?.result as Address | undefined;
    const fee = r1[i * 4 + 2]?.result as number | undefined;
    const spacing = r1[i * 4 + 3]?.result as number | undefined;
    if (t0) tokens.add(t0);
    if (t1) tokens.add(t1);
    return { pool: p, t0, t1, fee: fee ?? spacing ?? 0 };
  });

  // 2) symbol/decimals de los tokens únicos.
  const lista = [...tokens];
  const r2 = await client.multicall({
    multicallAddress: MULTICALL3,
    allowFailure: true,
    contracts: lista.flatMap((t) => [
      { address: t, abi: erc20Abi, functionName: "symbol" },
      { address: t, abi: erc20Abi, functionName: "decimals" },
    ]),
  });
  const sym = new Map<Address, string>();
  const dec = new Map<Address, number>();
  lista.forEach((t, i) => {
    sym.set(t, r2[i * 2]?.result as string);
    dec.set(t, r2[i * 2 + 1]?.result as number);
  });

  const out: Pool[] = [];
  for (const b of base) {
    if (!b.t0 || !b.t1) continue;
    const s0 = sym.get(b.t0);
    const s1 = sym.get(b.t1);
    const d0 = dec.get(b.t0);
    const d1 = dec.get(b.t1);
    if (s0 == null || s1 == null || d0 == null || d1 == null) continue;
    out.push({
      pool: b.pool,
      fee: b.fee,
      token0: { symbol: s0, address: b.t0, decimals: d0 },
      token1: { symbol: s1, address: b.t1, decimals: d1 },
      pair: `${s0}/${s1}`,
      tvlUsd: 0,
      aprPct: 0,
    });
  }
  return out;
}

/**
 * Las pools que se ofrecen al crear. El indexer del VPS escribe pools*.json; aqui se filtran por la
 * lista curada de la red. Si el indexer no está (beta), se leen los metadatos on-chain.
 *
 * ESA LISTA NO ES POR TAMAÑO, ES POR ORACULO. El vault exige que `observe(twapWindow)` responda en
 * `_requireHonestPrice`, y hay pools con fondos cuya cardinalidad es 1: el "TWAP" seria el spot,
 * sin proteccion ninguna, y en algunas `observe()` directamente revierte — abrir un vault ahi
 * fallaria en el acto. Por eso no se listan todas las del indice.
 */
export function usePools(red: RedKeel | undefined) {
  return useQuery({
    queryKey: ["pools", red?.clave],
    enabled: !!red,
    staleTime: 60_000,
    queryFn: async (): Promise<Pool[]> => {
      // 1) Índice del VPS (rápido, con TVL/APR) si está disponible.
      try {
        const r = await fetch(`/${red!.ficheroPools}?t=${Date.now()}`);
        if (r.ok) {
          const datos = (await r.json()) as { pools?: Pool[] };
          const todas = datos.pools ?? [];
          const permitidas = red!.poolsPermitidas;
          const filtradas = permitidas.length
            ? todas.filter((p) => permitidas.some((q) => esIgual(q, p.pool)))
            : todas;
          // Sanea TVL/APR del indexer: un dato roto (p.ej. TVL 1e34 de una pool sin oráculo) no debe
          // pintarse como cifra real. Fuera de rango → 0 (se muestra "—").
          const sane = (p: Pool): Pool => ({
            ...p,
            tvlUsd: Number.isFinite(p.tvlUsd) && p.tvlUsd >= 0 && p.tvlUsd < 1e12 ? p.tvlUsd : 0,
            aprPct: Number.isFinite(p.aprPct) && p.aprPct >= 0 && p.aprPct < 1e4 ? p.aprPct : 0,
            volumeYearUsd:
              Number.isFinite(p.volumeYearUsd) && (p.volumeYearUsd ?? 0) >= 0 && (p.volumeYearUsd ?? 0) < 1e15
                ? p.volumeYearUsd
                : 0,
          });
          if (filtradas.length) return filtradas.map(sane).sort((a, b) => (b.aprPct ?? 0) - (a.aprPct ?? 0));
        }
      } catch {
        /* sin índice: se cae al on-chain */
      }
      // 2) Fallback on-chain desde poolsPermitidas (beta sin indexer).
      return await poolsOnChain(red!);
    },
  });
}
